import { useState } from 'react';
import { Link } from 'react-router';
import { clsx } from 'clsx';
import { SEO, BreadcrumbSchema, HowToSchema } from '@/components/seo/seo';
import { useAuth } from '@/contexts/auth-context';

const steps = [
  {
    num: '01',
    title: 'Check Your Eligibility',
    subtitle: 'Free initial check',
    description: "Answer a few quick questions about your incident. Mac checks whether you may have the basis for a claim and identifies the authority likely to be responsible — your local council, National Highways, or another authority.",
    details: [
      'When and where did the damage occur?',
      'What type of damage to your vehicle?',
      'Do you have photos and repair costs?',
    ],
    outcome: 'You receive a general indication against common criteria, not legal advice or a prediction of the outcome.',
    icon: '✓',
    expandedContent: {
      title: 'Why eligibility matters',
      text: 'Not every pothole incident involves a highway maintained at public expense, and evidence requirements vary. Mac helps identify common threshold issues for you to review.',
    },
  },
  {
    num: '02',
    title: 'Gather Your Evidence',
    subtitle: 'Guided upload',
    description: "Upload your photos and documents. Mac checks each piece as you go — flagging anything unclear and confirming when you've got what you need.",
    details: [
      'Photos of the pothole (ideally with measurement)',
      'Photos of the damage to your vehicle',
      'Repair invoice or quote from a garage',
      'Location pinpointed on a map',
    ],
    outcome: 'Mac can check public prior-report sources. Any result is contextual information for you to review.',
    icon: '📁',
    expandedContent: {
      title: 'How prior reports may help',
      text: 'Mac checks FixMyStreet for potentially relevant prior reports near your location. A report may provide context about notice, but it does not by itself establish liability or defeat a Section 58 defence.',
    },
  },
  {
    num: '03',
    title: 'Get Your Claim Pack',
    subtitle: '£35, available now',
    description: 'Once you finish providing evidence, Mac prepares a claim letter and organised evidence bundle for you to review.',
    details: [
      'Structured claim letter based on the information you provide',
      'Organised evidence bundle',
      'Step-by-step submission guide for your specific council',
      'Know Your Rights briefing — general information on council duties',
    ],
    outcome: 'Download your pack, review it, and submit it yourself. You stay in control.',
    icon: '📄',
    expandedContent: {
      title: 'Professional documentation',
      text: 'Your claim letter is prepared from the information you provide and identifies relevant general legal context, including Section 41 of the Highways Act 1980. You review and edit it before submitting it yourself.',
    },
  },
  {
    num: '04',
    title: 'Submit & Manage Your Claim',
    subtitle: 'You handle submission and correspondence',
    description: "You send your claim to the authority using Fixtyer's guide, keep your own submission record, and manage all responses and negotiations. Fixtyer may provide general templates and process information.",
    details: [
      'Check the authority’s published response process',
      'General follow-up templates if you need them',
      'You decide how to respond to offers or rejections',
    ],
    outcome: 'You remain responsible for correspondence, deadlines, decisions and any negotiation.',
    icon: '📤',
    expandedContent: {
      title: 'What happens next',
      text: 'Authorities publish their own claims procedures and response information. You assess any response or offer and may wish to seek independent legal advice before deciding whether to accept, negotiate or take another step.',
    },
  },
  {
    num: '05',
    title: 'Escalate If Rejected',
    subtitle: 'Court escalation guidance',
    description: "If the council rejects your claim, don't give up — rejection isn't always the end of the process. Mac helps you understand your options while dedicated court document preparation is being developed.",
    details: [
      'Guidance for reviewing the rejection',
      'Escalation checklist',
      'Links to official small claims court resources',
      'Court document preparation planned for a later release',
    ],
    outcome: 'Court action involves fees, deadlines and risk. Review official guidance and consider independent legal advice.',
    icon: '⚖️',
    expandedContent: {
      title: 'Review court information carefully',
      text: 'The small claims track is designed for cases of limited value, but court action is still a legal process. Check current court rules, fees and deadlines, and seek qualified advice if you are unsure.',
    },
  },
];

const legalFacts = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    title: 'Section 41, Highways Act 1980',
    description: 'Section 41 concerns the duty to maintain highways maintainable at public expense. Whether that duty was breached is fact-specific.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
      </svg>
    ),
    title: 'What each side must show',
    description: 'A claimant must establish the relevant defect, damage and legal basis. If an authority relies on Section 58, it must establish the facts needed for that defence.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    title: 'Section 58 Defence',
    description: 'An authority may rely on evidence of reasonable inspection and maintenance. Prior reports can be relevant, but their effect depends on the specific facts.',
  },
];

const benefits = [
  {
    title: 'You Stay in Control',
    description: 'You submit, you decide, you claim. Mac prepares the documents — you handle your own case.',
    icon: '🎯',
  },
  {
    title: 'No Middlemen',
    description: 'No solicitors taking a cut, no claims management companies. Keep 100% of your compensation.',
    icon: '✂️',
  },
  {
    title: 'Professional Documents',
    description: 'Claim letters that cite the law correctly, organised evidence, clear submission guides.',
    icon: '📋',
  },
  {
    title: 'Prior Report Checks',
    description: 'We check public prior-report sources for contextual information you can review.',
    icon: '🔍',
  },
];

export function HowItWorksPage() {
  const [activeStep, setActiveStep] = useState(0);
  const currentStep = steps[activeStep];
  const { isAuthenticated } = useAuth();

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'How It Works', url: '/how-it-works' },
  ];

  const howToSteps = steps.map(s => ({
    name: s.title,
    text: s.description,
  }));

  return (
    <div className="relative">
      <SEO
        title="How It Works | Pothole Claim Process | Fixtyer"
        description="From pothole to claim submission in 5 clear steps. Learn how to prepare and submit a pothole damage claim with Fixtyer's guided process."
        canonical="/how-it-works"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <HowToSchema
        name="How to Claim for Pothole Damage"
        description="A complete 5-step guide to claiming compensation for pothole damage in the UK."
        steps={howToSteps}
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-accent-50 py-16 lg:py-24">
        {/* Top diagonal stripe bar */}
        <div 
          className="absolute top-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        {/* Background pattern */}
        <div className="absolute inset-0 opacity-30 mt-8">
          <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-brand-200" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Floating decorative elements */}
        <div className="absolute top-32 left-10 w-20 h-20 bg-accent-500/10 rounded-full blur-2xl" />
        <div className="absolute bottom-20 right-20 w-32 h-32 bg-brand-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-brand-600 mb-6">
            <Link to="/" className="hover:text-brand-700 transition-colors">Home</Link>
            <svg className="w-4 h-4 text-surface-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-surface-800">How It Works</span>
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-100 rounded-full text-accent-700 text-sm font-medium mb-6">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            5-step claim process
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-950 leading-tight mb-6">
            From Pothole to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-accent-600">
              Prepared Claim Pack
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-surface-800 max-w-2xl mx-auto mb-8">
            See how Fixtyer prepares documents and evidence for your review. You submit, manage and
            negotiate your own claim directly with the responsible authority.
          </p>

          {/* Quick stats */}
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <div className="flex items-center gap-2 text-brand-700">
              <svg className="w-5 h-5 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Free eligibility check
            </div>
            <div className="flex items-center gap-2 text-brand-700">
              <svg className="w-5 h-5 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              You stay in control
            </div>
            <div className="flex items-center gap-2 text-brand-700">
              <svg className="w-5 h-5 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              No hidden fees
            </div>
          </div>
        </div>

        {/* Bottom diagonal stripe bar */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* Journey Overview - Visual Timeline */}
      <section className="relative -mt-8 z-10 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-xl shadow-brand-900/10 border border-surface-200 p-6 sm:p-8">
            <div className="flex flex-wrap justify-between items-start gap-4">
              {steps.map((step, index) => (
                <button
                  key={index}
                  onClick={() => setActiveStep(index)}
                  className={clsx(
                    'group flex-1 min-w-[140px] text-center transition-all duration-300',
                    activeStep === index ? 'scale-105' : 'opacity-70 hover:opacity-100'
                  )}
                >
                  <div className="relative">
                    {/* Connector line */}
                    {index < steps.length - 1 && (
                      <div className="hidden sm:block absolute top-5 left-[60%] w-full h-0.5 bg-brand-100">
                        <div 
                          className={clsx(
                            'h-full transition-all duration-500',
                            activeStep > index ? 'bg-accent-500 w-full' : 'bg-brand-200 w-0'
                          )}
                        />
                      </div>
                    )}
                    
                    {/* Step circle */}
                    <div className={clsx(
                      'relative z-10 w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3 transition-all duration-300 text-lg',
                      activeStep === index 
                        ? 'bg-accent-500 text-white shadow-lg shadow-accent-500/30' 
                        : activeStep > index 
                          ? 'bg-success-500 text-white'
                          : 'bg-brand-200 text-brand-700'
                    )}>
                      {activeStep > index ? (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        step.icon
                      )}
                    </div>
                  </div>
                  
                  <p className={clsx(
                    'font-display font-semibold text-sm transition-colors',
                    activeStep === index ? 'text-brand-950' : 'text-brand-600'
                  )}>
                    {step.title}
                  </p>
                  <p className={clsx(
                    'text-xs mt-1 transition-colors',
                    activeStep === index ? 'text-accent-600' : 'text-brand-400'
                  )}>
                    {step.subtitle}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Steps Section */}
      <section className="py-16 bg-brand-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="mb-12 text-center">
            <span className="text-accent-500 text-sm font-semibold tracking-widest uppercase">
              THE PROCESS
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mt-3 mb-4">
              Step-by-Step Guide
            </h2>
            <p className="text-brand-300 text-lg max-w-2xl mx-auto">
              Click on each step to see exactly what's involved and what you'll need.
            </p>
          </div>

          {/* Steps Layout */}
          <div className="grid lg:grid-cols-[320px_1fr] gap-8">
            {/* Step Navigation */}
            <div className="flex flex-col">
              {steps.map((step, i) => (
                <button
                  key={i}
                  onClick={() => setActiveStep(i)}
                  className={clsx(
                    'relative p-5 text-left border border-brand-800 transition-all duration-200',
                    'hover:border-accent-500/50 hover:bg-accent-500/5',
                    activeStep === i && 'border-accent-500 bg-accent-500/10'
                  )}
                >
                  {/* Active indicator */}
                  {activeStep === i && (
                    <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-accent-500" />
                  )}
                  
                  <div className="flex items-center gap-4">
                    <span className={clsx(
                      'font-mono text-sm font-semibold',
                      activeStep === i ? 'text-accent-500' : 'text-brand-400'
                    )}>
                      {step.num}
                    </span>
                    <div>
                      <div className={clsx(
                        'font-semibold',
                        activeStep === i ? 'text-white' : 'text-brand-300'
                      )}>
                        {step.title}
                      </div>
                      <div className={clsx(
                        'text-sm mt-0.5',
                        activeStep === i ? 'text-accent-500' : 'text-brand-400'
                      )}>
                        {step.subtitle}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Step Detail */}
            <div className="p-8 lg:p-10 border border-brand-800 bg-gradient-to-br from-accent-500/5 to-transparent">
              {currentStep && (
                <div>
                  {/* Header */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-accent-500/10 border border-accent-500/30 flex items-center justify-center text-2xl text-accent-500">
                      {currentStep.icon}
                    </div>
                    <div>
                      <h3 className="text-2xl lg:text-3xl font-semibold text-white">
                        {currentStep.title}
                      </h3>
                      <span className="text-accent-500 text-sm font-medium">
                        {currentStep.subtitle}
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-brand-200 text-lg leading-relaxed mb-8">
                    {currentStep.description}
                  </p>

                  {/* What's involved */}
                  <div className="bg-black/30 p-6 mb-8">
                    <div className="text-xs font-semibold text-brand-500 uppercase tracking-wider mb-4">
                      What's involved
                    </div>
                    <ul className="space-y-3">
                      {currentStep.details.map((detail, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 bg-accent-500 rounded-full mt-2 flex-shrink-0" />
                          <span className="text-brand-100">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Outcome */}
                  <div className="flex items-start gap-3 p-5 bg-accent-500/10 border-l-[3px] border-accent-500 mb-8">
                    <span className="text-lg">💡</span>
                    <p className="text-brand-100 leading-relaxed">
                      {currentStep.outcome}
                    </p>
                  </div>

                  {/* Expanded Content */}
                  {currentStep.expandedContent && (
                    <div className="p-6 bg-brand-900/50 rounded-xl border border-brand-700">
                      <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                        <svg className="w-5 h-5 text-accent-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {currentStep.expandedContent.title}
                      </h4>
                      <p className="text-brand-300 text-sm leading-relaxed">
                        {currentStep.expandedContent.text}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile step indicator */}
          <div className="flex justify-center gap-1 mt-8 lg:hidden" role="tablist" aria-label="Process steps">
            {steps.map((step, i) => (
              <button
                key={i}
                onClick={() => setActiveStep(i)}
                className="w-11 h-11 flex items-center justify-center"
                aria-label={`Step ${i + 1}: ${step.title}`}
                aria-selected={activeStep === i}
                role="tab"
              >
                <span className={clsx(
                  'w-2.5 h-2.5 rounded-full transition-colors',
                  activeStep === i ? 'bg-accent-500' : 'bg-brand-700'
                )} />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Foundation Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-accent-500 text-sm font-semibold tracking-widest uppercase">
              THE LAW ON YOUR SIDE
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-950 mt-3 mb-4">
              Your Legal Foundation
            </h2>
            <p className="text-surface-800 text-lg max-w-2xl mx-auto">
              These are general explanations of the legal framework. Liability and any defence depend on the facts and evidence in each case.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {legalFacts.map((fact, index) => (
              <div 
                key={index}
                className="relative group p-8 bg-gradient-to-br from-brand-50 to-white rounded-2xl border-2 border-brand-100 hover:border-brand-300 transition-all duration-300 hover:shadow-xl hover:shadow-brand-900/5"
              >
                <div className="w-14 h-14 bg-brand-100 rounded-xl flex items-center justify-center text-brand-600 mb-5 group-hover:bg-brand-600 group-hover:text-white transition-colors duration-300">
                  {fact.icon}
                </div>
                <h3 className="font-display text-xl font-semibold text-brand-950 mb-3">
                  {fact.title}
                </h3>
                <p className="text-surface-800 leading-relaxed">
                  {fact.description}
                </p>
              </div>
            ))}
          </div>

          {/* Quote callout */}
          <div className="mt-12 p-8 bg-brand-950 rounded-3xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-accent-500/10 rounded-full blur-3xl" />
            <div className="relative flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-accent-500/20 rounded-2xl flex items-center justify-center">
                  <svg className="w-10 h-10 text-accent-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>
              </div>
              <div>
                <p className="text-xl text-white font-medium leading-relaxed mb-4">
                  A claimant and the highway authority each need evidence for the matters they rely on.
                  Section 58 is a fact-specific defence, and general information cannot determine an individual outcome.
                </p>
                <p className="text-accent-400 font-semibold">
                  General information on Sections 41 and 58, Highways Act 1980
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-brand-50 via-white to-accent-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-accent-500 text-sm font-semibold tracking-widest uppercase">
              WHY FIXTYER
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-950 mt-3 mb-4">
              The Fixtyer Difference
            </h2>
            <p className="text-surface-800 text-lg max-w-2xl mx-auto">
              We built this to be the service we'd want to use ourselves — simple, transparent, and empowering.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="relative p-6 bg-white rounded-2xl shadow-lg shadow-brand-900/5 border border-surface-200 hover:border-accent-300 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="text-4xl mb-4">{benefit.icon}</div>
                <h3 className="font-display text-lg font-semibold text-brand-950 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-surface-800 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Expectations */}
      <section className="py-20 bg-brand-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-accent-500 text-sm font-semibold tracking-widest uppercase">
              WHAT TO EXPECT
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mt-3 mb-4">
              Process Checkpoints
            </h2>
            <p className="text-brand-300 text-lg max-w-2xl mx-auto">
              Timing varies by authority, evidence, correspondence and any later legal process.
            </p>
          </div>

          <div className="space-y-6">
            <div className="flex items-center gap-6 p-6 bg-brand-900/50 rounded-2xl border border-brand-800">
              <div className="flex-shrink-0 w-16 h-16 bg-accent-500/20 rounded-xl flex items-center justify-center">
                <span className="text-2xl">⏱️</span>
              </div>
              <div>
                <h4 className="font-display text-lg font-semibold text-white mb-1">Start to Submission</h4>
                <p className="text-brand-300">Gather evidence and prepare your pack at your own pace.</p>
              </div>
            </div>

            <div className="flex items-center gap-6 p-6 bg-brand-900/50 rounded-2xl border border-brand-800">
              <div className="flex-shrink-0 w-16 h-16 bg-accent-500/20 rounded-xl flex items-center justify-center">
                <span className="text-2xl">📬</span>
              </div>
              <div>
                <h4 className="font-display text-lg font-semibold text-white mb-1">Council Response</h4>
                <p className="text-brand-300">Check the responsible authority&apos;s published process and keep your own records of correspondence.</p>
              </div>
            </div>

            <div className="flex items-center gap-6 p-6 bg-brand-900/50 rounded-2xl border border-brand-800">
              <div className="flex-shrink-0 w-16 h-16 bg-accent-500/20 rounded-xl flex items-center justify-center">
                <span className="text-2xl">📝</span>
              </div>
              <div>
                <h4 className="font-display text-lg font-semibold text-white mb-1">Authority Response</h4>
                <p className="text-brand-300">You review and respond to any request, rejection or offer. Fixtyer does not negotiate or decide for you.</p>
              </div>
            </div>

            <div className="flex items-center gap-6 p-6 bg-brand-900/50 rounded-2xl border border-brand-800">
              <div className="flex-shrink-0 w-16 h-16 bg-accent-500/20 rounded-xl flex items-center justify-center">
                <span className="text-2xl">⚖️</span>
              </div>
              <div>
                <h4 className="font-display text-lg font-semibold text-white mb-1">Court (If Needed)</h4>
                <p className="text-brand-300">Review current court guidance, fees, deadlines and risks before deciding whether to proceed.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-brand-600 to-brand-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white/90 text-sm font-medium mb-6">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Free eligibility check — no commitment
          </div>
          
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to pursue a pothole damage claim?
          </h2>
          <p className="text-xl text-brand-100 mb-8 max-w-2xl mx-auto">
            Start with the free check, then prepare documents and evidence for your own review and submission.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={isAuthenticated ? "/dashboard" : "/check"}
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-brand-700 font-semibold rounded-xl hover:bg-brand-50 transition-all hover:shadow-lg"
            >
              {isAuthenticated ? "Go to Dashboard" : "Start Your Free Claim Check"}
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/faq"
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white font-semibold rounded-xl border-2 border-white/30 hover:bg-white/10 transition-colors"
            >
              Read FAQs
            </Link>
          </div>

          {/* Trust indicators */}
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-brand-100 text-sm">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              No hidden fees
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              You stay in control
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Keep 100% of compensation
            </div>
          </div>
        </div>
      </section>

      {/* Bottom diagonal stripe bar */}
      <div 
        className="h-8"
        style={{
          background: `repeating-linear-gradient(
            -45deg,
            #1e40af,
            #1e40af 40px,
            #f97316 40px,
            #f97316 80px
          )`
        }}
      />
    </div>
  );
}
